import { autoinject, transient } from "aurelia-dependency-injection";
import { Claims } from "../../../../../constants/authentication/claims";
import { type StepModel } from "../../../../../models/basics/basics.models";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { BasePaginatedFilterableVesselPortList } from "../models/port-risk-profile.models";
import { PortRiskProfileTabsEnum } from "../models/ports-and-terminals.models";
import { PortsAndTerminalsService } from "../services/ports-and-terminals.service";
import { VesselsDeparted } from "./tabs/vessels-departed/vessels-departed";
import { VesselsInPort } from "./tabs/vessels-in-port/vessels-in-port";
import { VesselsPreArrival } from "./tabs/vessels-pre-arrival/vessels-pre-arrival";
import { computedFrom, observable } from "aurelia-framework";
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { Reflection } from "../../../../../utilities/reflection";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { MenuItem } from "../../../../components/menu/menu-item";

@autoinject
@transient()
export class PortRiskProfile {
    private _ptBuCacheKey: string = "pt-bu-setting";
    private _ptLastVisitedTabCacheKey: string = "pt-last-visited-tab";

    public previousCustomerId: number;
    public businessUnitId: number;
    public tabs: StepModel[] = [];
    public isRightShipUser: boolean = false;

    /* Currently selected customer id */
    public customerId: number;

    /* Currently displayed tab */
    @observable()
    public currentTab: StepModel = null;

    @computedFrom(Reflection.getPropertyPath<PortRiskProfile>(x => x.customerId))
    public get customerFilterActive() {
        return this.customerId != null;
    }

    @computedFrom(Reflection.getPropertyPath<PortRiskProfile>(x => x.businessUnitId))
    public get businessUnitFilterActive() {
        return this.businessUnitId;
    }

    @computedFrom(Reflection.getPropertyPath<PortRiskProfile>(x => x.customerId))
    public get selectedCustomerFilter() {
        if (this.customerId) {
            return '{ customerId : ' + this.customerId.toString() + '}';
        }
        else return null;
    }

    constructor(private vesselsPreArrival: VesselsPreArrival,
        private vesselsInPort: VesselsInPort,
        private vesselsDeparted: VesselsDeparted,
        private userClaimsService: UserClaimsService,
        private portsAndTerminalsService: PortsAndTerminalsService,
        private cacheClient: CacheClientService,
        private dialogClientService: DialogClientService) {
        this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
    }

    async activate() {
        await this.getCachedBuSetting();
        await this.initializeTabs();
    }

    private async initializeTabs() {
        const getModel = () => { };
        const tabs: StepModel[] = [{
            title: 'Pre-Arrivals',
            step: PortRiskProfileTabsEnum.PreArrivals,
            show: true,
            vm: this.vesselsPreArrival,
            getModel: getModel
        }, {
            title: 'In Port',
            step: PortRiskProfileTabsEnum.InPort,
            show: true,
            vm: this.vesselsInPort,
            getModel: getModel
        }, {
            title: 'Departed',
            step: PortRiskProfileTabsEnum.Departed,
            show: true,
            vm: this.vesselsDeparted,
            getModel: getModel
        }];

        this.tabs = tabs;

        const cache = await this.cacheClient.get<{ step: number }>(this._ptLastVisitedTabCacheKey, CacheLocation.LocalStorage, false);
        if (cache && cache.step) {
            this.currentTab = this.tabs.filter(x => x.step == cache.step)[0];
            this.refreshTabs();
        }
    }

    /* Toggle BU filter */
    toggleBusinessUnitFilters() {
        this.portsAndTerminalsService.setBusinessUnit(this.businessUnitId);
    }

    /**
    * Refresh tabs data
    */
    refreshTabs() {
        //Refresh current tab
        if (this.currentTab?.vm instanceof BasePaginatedFilterableVesselPortList) {
            this.currentTab.vm.refresh();
        }

        this.tabs.forEach((tab: StepModel) => {
            if (tab?.vm instanceof BasePaginatedFilterableVesselPortList) {
                tab.vm.customerId = this.customerId;
                tab.vm.businessUnitId = this.businessUnitId;
            }
        });
    }

    /* Clear customer filter */
    clearCustomerFilter() {
        this.customerId = null;

        if (this.businessUnitId) {
            this.businessUnitId = undefined;
            this.portsAndTerminalsService.setBusinessUnit(undefined);

            this.refreshTabs();
        }
    }

    /* Clear bu filter */
    clearBusinessUnitFilters() {
        this.businessUnitId = undefined;

        this.portsAndTerminalsService.setBusinessUnit(undefined);

        this.refreshTabs();
    }

    /* Apply customer filter */
    applyCustomerFilter() {

        //If a new customer is selected, clear business unit filters
        if (this.previousCustomerId !== this.customerId) {
            //set new customer to previousCustomerId
            const cust = Object.assign({}, { customerId: this.customerId });
            this.previousCustomerId = cust.customerId;

            this.clearBusinessUnitFilters();
        }
        this.cacheBuSetting();
    }

    applyBusinessUnitFilters() {
        this.toggleBusinessUnitFilters();

        this.refreshTabs();
        this.cacheBuSetting();
    }

    showPortCallFileUploads() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PortCallFileUploadsDialog, { }, true, false, false, "right");
    }

    currentTabChanged() {
        if (this.currentTab) {
            this.cacheClient.put(this._ptLastVisitedTabCacheKey, { step: this.currentTab.step });
        }
    }

    private cacheBuSetting() {
        if (this.isRightShipUser) {
            this.cacheClient.put(this._ptBuCacheKey, { customerId: this.customerId, businessUnitId: this.businessUnitId });
        }
    }

    private async getCachedBuSetting() {
        if (this.isRightShipUser) {
            const response = await this.cacheClient.get<{ customerId: number, businessUnitId: number }>(this._ptBuCacheKey, CacheLocation.LocalStorage, false);
            if (response) {
                this.customerId = response.customerId;
                this.previousCustomerId = response.customerId;
                this.businessUnitId = response.businessUnitId;
            }
        }
    }

    @computedFrom("item")
    get menuItems(): MenuItem[] {
        return [
            {
                click: () => this.showPortCallFileUploads(),
                isVisible: true,
                item: {
                    docLinesIcon: true,
                    text: 'Vessel list uploads'
                },
                classList: '',
            }
        ];
    }
}
